import { useState, useEffect } from 'react';
import {
  ShoppingCart,
  TrendingUp,
  FileText,
  Calendar,
  DollarSign,
  Truck,
  ClipboardList,
  AlertCircle,
  CreditCard,
  Building,
  ArrowRight,
  Users,
  Package,
  CalendarDays
} from 'lucide-react';
import api from '../api/config';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import '../styles/Dashboard.css';

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const Dashboard = () => {
  const [currency, setCurrency] = useState('CLP');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const username = localStorage.getItem('username') || 'Usuario';

  const toggleMonth = (m) => {
    setSelectedMonths(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
    );
  };

  useEffect(() => {
    loadDashboardStats();
  }, [selectedYear, selectedMonths]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedYear) params.year = selectedYear;
      if (selectedMonths.length > 0) params.month = selectedMonths.join(',');
      const response = await api.get('/matriz/dashboard/stats', { params });
      setDashboardStats(response.data);
      if (response.data.availableYears && response.data.availableYears.length > 0) {
        setAvailableYears(response.data.availableYears);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tasas de conversión actuales (Enero 2025)
  const exchangeRates = {
    CLP: 1,
    USD: 950, // 1 USD = 950 CLP
    UF: 36500 // 1 UF = 36500 CLP
  };

  const convertCurrency = (amountCLP, toCurrency, onlyValue = false) => {
    const amount = parseFloat(amountCLP.replace(/[^0-9.-]+/g, ''));
    if (toCurrency === 'CLP') {
      return onlyValue ? amount.toLocaleString('es-CL') : `$${amount.toLocaleString('es-CL')}`;
    } else if (toCurrency === 'USD') {
      const usdAmount = (amount / exchangeRates.USD);
      const formattedUSD = usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return onlyValue ? formattedUSD : `US$${formattedUSD}`;
    } else if (toCurrency === 'UF') {
      const ufAmount = (amount / exchangeRates.UF);
      const formattedUF = ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
      return onlyValue ? formattedUF : `${formattedUF} UF`;
    }
    return amountCLP;
  };

  const getCurrencySymbol = (currency) => {
    if (currency === 'CLP') return '$';
    if (currency === 'USD') return 'US$';
    if (currency === 'UF') return 'UF';
    return '$';
  };

  const stats = dashboardStats ? [
    {
      title: 'Órdenes en Maestro',
      value: dashboardStats.maestroSupply.total_ordenes || '0',
      icon: ClipboardList,
      change: `${dashboardStats.maestroSupply.clientes_unicos || 0} clientes`,
      color: 'stat-blue'
    },
    {
      title: `Volumen Total (${getCurrencySymbol(currency)})`,
      value: convertCurrency(`$${parseFloat(dashboardStats.maestroSupply.volumen_ventas || 0)}`, currency, true),
      icon: DollarSign,
      change: `Utilidad: ${getCurrencySymbol(currency)}${parseFloat(dashboardStats.maestroSupply.utilidad_total || 0).toLocaleString('es-CL')}`,
      color: 'stat-green'
    },
    {
      title: 'Margen Promedio',
      value: `${parseFloat(dashboardStats.maestroSupply.margen_promedio || 0).toFixed(1)}%`,
      icon: TrendingUp,
      change: `${dashboardStats.maestroSupply.proveedores_unicos || 0} proveedores`,
      color: 'stat-purple'
    },
    {
      title: 'Clientes Activos',
      value: dashboardStats.businessPartners.clientes_activos || '0',
      icon: Users,
      change: `${dashboardStats.businessPartners.proveedores_activos || 0} proveedores`,
      color: 'stat-orange'
    }
  ] : [
    {
      title: 'Órdenes en Maestro',
      value: '0',
      icon: ClipboardList,
      change: 'Cargando...',
      color: 'stat-blue'
    },
    {
      title: 'Volumen Total',
      value: '0',
      icon: DollarSign,
      change: 'Cargando...',
      color: 'stat-green'
    },
    {
      title: 'Margen Promedio',
      value: '0%',
      icon: TrendingUp,
      change: 'Cargando...',
      color: 'stat-purple'
    },
    {
      title: 'Clientes Activos',
      value: '0',
      icon: Users,
      change: 'Cargando...',
      color: 'stat-orange'
    }
  ];

  const recentOrders = [
    { id: 'OV-2025-001', customer: 'Minera Escondida', date: '2025-01-14', amount: 23450000, description: 'Válvulas alta presión WEIR 3000PSI', status: 'Completado' },
    { id: 'OV-2025-002', customer: 'Codelco División Andina', date: '2025-01-14', amount: 56780000, description: 'Repuestos Komatsu 930E-4', status: 'En Proceso' },
    { id: 'OV-2025-003', customer: 'Anglo American', date: '2025-01-13', amount: 8900000, description: 'EPP certificado para altura', status: 'Pendiente' },
    { id: 'OV-2025-004', customer: 'BHP Billiton', date: '2025-01-13', amount: 123450000, description: 'Sistema de filtrado para planta', status: 'Completado' },
    { id: 'OV-2025-005', customer: 'Antofagasta Minerals', date: '2025-01-12', amount: 45678000, description: 'Correas transportadoras 72"', status: 'En Proceso' },
  ];

  const salesData = [
    { day: 'Lun', ventas: 2400000 },
    { day: 'Mar', ventas: 1398000 },
    { day: 'Mié', ventas: 3800000 },
    { day: 'Jue', ventas: 3908000 },
    { day: 'Vie', ventas: 4800000 },
    { day: 'Sáb', ventas: 3800000 },
    { day: 'Dom', ventas: 4300000 },
  ];

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Bienvenido de vuelta, {username}. Aquí está el resumen de hoy.</p>
      </div>

      {/* Filtros de Año y Mes */}
      <div className="dashboard-date-filters">
        <div className="date-filter-group">
          <CalendarDays size={16} />
          <span className="filter-label">Año:</span>
          <div className="year-pills">
            <button
              className={`year-pill ${selectedYear === '' ? 'active' : ''}`}
              onClick={() => setSelectedYear('')}
            >
              Todos
            </button>
            {availableYears.map(year => (
              <button
                key={year}
                className={`year-pill ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>
          <span className="filter-separator">|</span>
          <span className="filter-label">Mes:</span>
          <div className="month-pills">
            {MONTH_NAMES.map((name, i) => (
              <button
                key={i + 1}
                className={`month-pill ${selectedMonths.includes(i + 1) ? 'active' : ''}`}
                onClick={() => toggleMonth(i + 1)}
              >
                {name}
              </button>
            ))}
          </div>
          {selectedMonths.length > 0 && (
            <button className="clear-months-btn" onClick={() => setSelectedMonths([])}>
              Limpiar
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">
              <stat.icon size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-title">{stat.title}</p>
              <h3 className="stat-value">{stat.value}</h3>
              <span className="stat-change">{stat.change}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <div className="card recent-orders">
          <div className="card-header">
            <h2>Top 5 Clientes por Volumen</h2>
          </div>
          {loading ? (
            <div style={{padding: '20px', textAlign: 'center'}}>Cargando...</div>
          ) : dashboardStats && dashboardStats.topClients.length > 0 ? (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Órdenes</th>
                    <th>Total Ventas</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardStats.topClients.map((client, index) => (
                    <tr key={index} className="order-row">
                      <td>
                        <div className="client-cell">
                          <Building size={16} />
                          {client.nombre}
                        </div>
                      </td>
                      <td>{client.ordenes}</td>
                      <td className="order-amount">
                        {convertCurrency(`$${parseFloat(client.total_ventas || 0)}`, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{padding: '20px', textAlign: 'center', color: '#999'}}>
              No hay datos de clientes disponibles
            </div>
          )}
        </div>

        <div className="card recent-orders">
          <div className="card-header">
            <h2>Top 5 Proveedores por Volumen</h2>
          </div>
          {loading ? (
            <div style={{padding: '20px', textAlign: 'center'}}>Cargando...</div>
          ) : dashboardStats && dashboardStats.topSuppliers.length > 0 ? (
            <div className="orders-table">
              <table>
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>Órdenes</th>
                    <th>Total Compras</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardStats.topSuppliers.map((supplier, index) => (
                    <tr key={index} className="order-row">
                      <td>
                        <div className="client-cell">
                          <Package size={16} />
                          {supplier.nombre}
                        </div>
                      </td>
                      <td>{supplier.ordenes}</td>
                      <td className="order-amount">
                        {convertCurrency(`$${parseFloat(supplier.total_compras || 0)}`, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{padding: '20px', textAlign: 'center', color: '#999'}}>
              No hay datos de proveedores disponibles
            </div>
          )}
        </div>
      </div>

      {/* Resumen adicional */}
      {dashboardStats && (
        <div className="card" style={{marginTop: '20px', padding: '20px'}}>
          <h2>Resumen General</h2>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px'}}>
            <div>
              <p style={{color: '#666', fontSize: '14px'}}>Total Órdenes</p>
              <p style={{fontSize: '24px', fontWeight: 'bold'}}>{dashboardStats.maestroSupply.total_ordenes || 0}</p>
            </div>
            <div>
              <p style={{color: '#666', fontSize: '14px'}}>Volumen Ventas</p>
              <p style={{fontSize: '24px', fontWeight: 'bold', color: '#10b981'}}>
                {convertCurrency(`$${parseFloat(dashboardStats.maestroSupply.volumen_ventas || 0)}`, currency)}
              </p>
            </div>
            <div>
              <p style={{color: '#666', fontSize: '14px'}}>Costo Total</p>
              <p style={{fontSize: '24px', fontWeight: 'bold', color: '#ef4444'}}>
                {convertCurrency(`$${parseFloat(dashboardStats.maestroSupply.costo_total || 0)}`, currency)}
              </p>
            </div>
            <div>
              <p style={{color: '#666', fontSize: '14px'}}>Utilidad Total</p>
              <p style={{fontSize: '24px', fontWeight: 'bold', color: '#3b82f6'}}>
                {convertCurrency(`$${parseFloat(dashboardStats.maestroSupply.utilidad_total || 0)}`, currency)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;